import { type FC } from 'react'

import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import { FormSwitch } from '@/components/ui/form/form-switch.tsx'
import {
  FORM_ID,
  type FormValues,
} from '@/dialogs/TranslationConfigDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import {
  $chatterLanguage,
  $fanLanguage,
  $isAutoTranslationsEnabled,
  $isTranslatorEnabled,
} from '@/models/chattingUI/messages/translations.ts'
import { useChats } from '@/modules/chatting/state/chats-provider.ts'
import { languages } from '@/modules/chatting/ui/chat-window/translation/consts.ts'
import { isSupportedLanguage } from '@/modules/chatting/ui/chat-window/translation/utils.ts'
import { classNames } from '@/utils/helpers.ts'

const languageOptions: SelectOptionsType = languages.map(({ code, name }) => ({
  label: name,
  value: code,
}))

export interface Props extends DialogProps {}

export const TranslationConfigDialog: FC<Props> = ({ isOpen, onClose }) => {
  const fanLanguage = useUnit($fanLanguage)
  const isAutoTranslationsEnabled = useUnit($isAutoTranslationsEnabled)
  const isTranslatorEnabled = useUnit($isTranslatorEnabled)
  const chatterLanguage = useUnit($chatterLanguage)

  const {
    enableAutoTranslation,
    disableAutoTranslation,
    setOwnLanguage,
    enableTranslator,
    disableTranslator,
    setFanLanguage,
    openTranslationTutorial,
  } = useChats()
  const form = useForm<FormValues>({
    defaultValues: {
      ownLanguage: chatterLanguage!,
      fanLanguage,
      enabled: isAutoTranslationsEnabled,
      translatorEnabled: isTranslatorEnabled,
    },
  })

  const onSubmit = (values: FormValues) => {
    if (
      form.formState.dirtyFields.fanLanguage &&
      isSupportedLanguage(values.fanLanguage!)
    ) {
      setFanLanguage(values.fanLanguage)
    }
    if (
      form.formState.dirtyFields.ownLanguage &&
      isSupportedLanguage(values.ownLanguage)
    ) {
      setOwnLanguage(values.ownLanguage)
    }
    if (form.formState.dirtyFields.enabled) {
      ;(values.enabled ? enableAutoTranslation : disableAutoTranslation)()
    }
    if (form.formState.dirtyFields.translatorEnabled) {
      if (values.translatorEnabled) {
        enableTranslator()
      } else {
        disableTranslator()
      }
    }
    onClose()
    openTranslationTutorial()
  }

  const isDisabled = !form.watch('translatorEnabled')

  return (
    <Dialog
      bigDialog
      show={isOpen}
      afterLeave={form.reset}
      onClose={onClose}
      panelClassName="lg:max-w-[700px] sm:p-8"
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>AI Language Translator</Dialog.Title>
          <Dialog.Description className="text-center">
            First choose your language and then choose the language that your
            fan speaks so your messages get translated as well.
          </Dialog.Description>
        </Dialog.Header>
        <FormProvider {...form}>
          <form
            id={FORM_ID}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormSwitch
              name="translatorEnabled"
              control={form.control}
              labelBeforeSwitch
              label="AI Translator"
              labelClassName="justify-between"
              tooltip="Turn the AI Translator on or off"
            />
            <hr className="my-5" />
            <FormSelect
              name="ownLanguage"
              label="I read in"
              description="Messages from the fan are shown in this language. Applies to all chats."
              options={languageOptions}
              placeholder="Select language"
              disabled={isDisabled}
            />
            <hr className="my-5" />
            <FormSelect
              name="fanLanguage"
              label="Fan reads in"
              description="Messages you send are translated into this language when you use the translate button. Saved per chat."
              options={languageOptions}
              placeholder="No language selected"
              className="mb-6"
              disabled={isDisabled}
            />
            <FormSwitch
              name="enabled"
              control={form.control}
              labelBeforeSwitch
              label="Automatically translate all messages of this fan"
              labelClassName="justify-between"
              disabled={isDisabled}
              className={classNames(
                isDisabled && 'cursor-not-allowed opacity-50',
              )}
            />
          </form>
        </FormProvider>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={!form.formState.isDirty}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
